/**
 * PhotoGrid Component
 * Responsive masonry-style photo grid with loading and empty states
 * Displays photos with optional loading skeletons
 *
 * @module components/gallery/PhotoGrid
 */

import React, { useEffect } from 'react';
import { Photo, User } from '@/types';
import { logger } from '@/utils/logger';
import { Skeleton } from '@/components/common';
import PhotoCard from './PhotoCard';

// ============================================================================
// TYPES
// ============================================================================

interface PhotoGridProps {
  photos: Photo[];
  currentUser?: User | null;
  onPhotoClick: (photo: Photo) => void;
  onDeletePhoto?: (photoId: string) => void;
  isLoading?: boolean;
  isEmpty?: boolean;
  deletingPhotoId?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * Photo grid component displaying photos in responsive masonry layout
 * Shows loading skeletons while loading and empty state when no photos
 * Adapts to different screen sizes with Tailwind CSS grid
 *
 * @component
 * @example
 * <PhotoGrid
 *   photos={photos}
 *   currentUser={user}
 *   onPhotoClick={handlePhotoClick}
 *   onDeletePhoto={handleDelete}
 *   isLoading={isLoading}
 *   isEmpty={!isLoading && photos.length === 0}
 * />
 */
const PhotoGrid: React.FC<PhotoGridProps> = ({
  photos,
  currentUser,
  onPhotoClick,
  onDeletePhoto,
  isLoading = false,
  isEmpty = false,
  deletingPhotoId,
}) => {
  // Debug logging
  useEffect(() => {
    logger.debug(`[WALRUS] PhotoGrid: mounted`, {
      photoCount: photos.length,
      isLoading,
      isEmpty,
    });
  }, [photos.length, isLoading, isEmpty]);

  // Render loading skeleton grid
  if (isLoading) {
    logger.debug(`[WALRUS] PhotoGrid: rendering loading state`);
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        {Array.from({ length: 12 }).map((_, index) => (
          <div key={`skeleton-${index}`} className="rounded-lg overflow-hidden bg-walrus-cream border border-walrus-gray">
            <Skeleton
              width="100%"
              height="200px"
              rounded
              className="aspect-square"
            />
            <div className="p-3 space-y-2">
              <Skeleton width="80%" height="16px" rounded />
              <Skeleton width="60%" height="12px" rounded />
              <Skeleton width="70%" height="12px" rounded />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Render empty state
  if (isEmpty || photos.length === 0) {
    logger.debug(`[WALRUS] PhotoGrid: rendering empty state`);
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-walrus-gray bg-walrus-cream px-8 py-16">
        <svg
          className="mb-4 h-12 w-12 text-walrus-gray"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <h3 className="mb-2 text-lg font-medium text-walrus-black">No photos yet</h3>
        <p className="text-walrus-silver">
          Start by uploading your first family photo to the gallery!
        </p>
      </div>
    );
  }

  logger.debug(`[WALRUS] PhotoGrid: rendering ${photos.length} photos`);

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
      {photos.map((photo) => (
        <PhotoCard
          key={photo.id}
          photo={photo}
          currentUser={currentUser}
          onPhotoClick={onPhotoClick}
          onDelete={onDeletePhoto}
          isDeleting={deletingPhotoId === photo.id}
        />
      ))}
    </div>
  );
};

PhotoGrid.displayName = 'PhotoGrid';

export default PhotoGrid;
